import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../Utils/axiosInstance";
import { toast } from "react-hot-toast";

// GET ALL GAMES
export const getAllGames = createAsyncThunk(
  "game/getAllGames",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get("/getAllGames");
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// GET ALL GAMES MINIMAL
export const getAllGamesMinimal = createAsyncThunk(
  "game/getAllGamesMinimal",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get("/getAllGamesMinimal");
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const getAllActiveGames = createAsyncThunk(
  "game/getAllActiveGames",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get("/getAllActiveGames");
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// CREATE GAME
export const createGame = createAsyncThunk(
  "game/createGame",
  async ({ formData, onProgress }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post("/createGame", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const total = progressEvent.total || progressEvent.loaded;
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / total
            );
            onProgress(percentCompleted);
          }
        },
      });
      toast.success("Game Add successful");
      return res.data;
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || err.message || "Something went wrong";
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// UPDATE GAME
export const updateGame = createAsyncThunk(
  "game/updateGame",
  async ({ _id, formData, onProgress }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`/updateGame/${_id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const total = progressEvent.total || progressEvent.loaded;
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / total
            );
            onProgress(percentCompleted);
          }
        },
      });
      toast.success("Game Update successful");
      return res.data.data; // controller returns { message, data }
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || err.message || "Something went wrong";
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// DELETE GAME
export const deleteGame = createAsyncThunk(
  "game/deleteGame",
  async ({ _id }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.delete(`/deleteGame/${_id}`);
      toast.success("Game Delete successful");
      return { _id };
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || "Something went wrong";
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// RESTORE GAME
export const restoreGame = createAsyncThunk(
  "game/restoreGame",
  async ({ _id }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`/restoreGame/${_id}`);
      toast.success("Game Restore successful");
      return res.data.data;
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || "Something went wrong";
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// get by Id
export const getGameById = createAsyncThunk(
  "game/getById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/getGameById/${id}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);
export const getLatestGamesByCategory = createAsyncThunk(
  "game/getLatestGamesByCategory",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/getLatestGamesByCategory`);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const getAllActiveGamesWithPagination = createAsyncThunk(
  "game/getAllActiveGamesWithPagination",
  async ({ page = 1, limit = 15, category = "all", search = "", isActive = null, userside, discountId, platform, sortBy, isDeleted, filterUser, filterDate }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(
        `/getAllActiveGamesWithPagination`,
        {
          params: {
            page,
            limit,
            category,
            search,
            isActive,
            userside,
            platform,
            sortBy,
            isDeleted,
            filterUser,
            filterDate,
            ...(discountId ? { discountId } : {}),
          },
        }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);
export const getNew10Games = createAsyncThunk(
  "game/getNew10Games",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/getNew10Games`);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const getCloudGames = createAsyncThunk(
  "game/getCloudGames",
  async ({ page = 1, limit = 25, category, search = "", isActive = true }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(
        `/getAllActiveGamesWithPagination`,
        {
          params: {
            page,
            limit,
            category,
            search,
            isActive
          },
        }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

const gameSlice = createSlice({
  name: "game",
  initialState: {
    games: [],
    LatestGamesByCategory: [],
    cloudGames: [],
    singleGame: null,
    // Individual loading states for each API call
    loadingStates: {
      getAllGames: false,
      getAllGamesMinimal: false,
      getAllActiveGames: false,
      createGame: false,
      updateGame: false,
      deleteGame: false,
      restoreGame: false,
      getGameById: false,
      getLatestGamesByCategory: false,
      getAllActiveGamesWithPagination: false,
      new10Games: false,
      getCloudGames: false,
    },
    error: null,
    success: null,
    paginatedGames: [],
    new10Games: [],
    totalGames: 0,
    currentPage: 1,
    totalPages: 1,
  },
  reducers: {
    clearGameError: (state) => {
      state.error = null;
    },
    clearGameSuccess: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET ALL
      .addCase(getAllGames.pending, (state) => {
        state.loadingStates.getAllGames = true;
        state.error = null;
      })
      .addCase(getAllGames.fulfilled, (state, action) => {
        state.loadingStates.getAllGames = false;
        state.games = action.payload;
      })
      .addCase(getAllGames.rejected, (state, action) => {
        state.loadingStates.getAllGames = false;
        state.error = action.payload;
      })
      // GET ALL MINIMAL
      .addCase(getAllGamesMinimal.pending, (state) => {
        state.loadingStates.getAllGamesMinimal = true;
        state.error = null;
      })
      .addCase(getAllGamesMinimal.fulfilled, (state, action) => {
        state.loadingStates.getAllGamesMinimal = false;
        state.games = action.payload;
      })
      .addCase(getAllGamesMinimal.rejected, (state, action) => {
        state.loadingStates.getAllGamesMinimal = false;
        state.error = action.payload;
      })
      // GET ALL ACTIVE
      .addCase(getAllActiveGames.pending, (state) => {
        state.loadingStates.getAllActiveGames = true;
        state.error = null;
      })
      .addCase(getAllActiveGames.fulfilled, (state, action) => {
        state.loadingStates.getAllActiveGames = false;
        state.games = action.payload;
      })
      .addCase(getAllActiveGames.rejected, (state, action) => {
        state.loadingStates.getAllActiveGames = false;
        state.error = action.payload;
      })
      // GET 10 New ACTIVE Game
      .addCase(getNew10Games.pending, (state) => {
        state.loadingStates.new10Games = true;
        state.error = null;
      })
      .addCase(getNew10Games.fulfilled, (state, action) => {
        state.loadingStates.new10Games = false;
        state.new10Games = action.payload;
      })
      .addCase(getNew10Games.rejected, (state, action) => {
        state.loadingStates.new10Games = false;
        state.error = action.payload;
      })
      // CREATE
      .addCase(createGame.pending, (state) => {
        state.loadingStates.createGame = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createGame.fulfilled, (state, action) => {
        state.loadingStates.createGame = false;
        state.success = "Game created successfully";
        state.games.unshift(action.payload); // add new game to top
        state.paginatedGames.unshift(action.payload); // add new game to top
      })
      .addCase(createGame.rejected, (state, action) => {
        state.loadingStates.createGame = false;
        state.error = action.payload;
      })
      // UPDATE
      .addCase(updateGame.pending, (state) => {
        state.loadingStates.updateGame = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateGame.fulfilled, (state, action) => {
        state.loadingStates.updateGame = false;
        state.success = "Game updated successfully";
        const idx = state.games.findIndex((g) => g._id === action.payload._id);
        if (idx !== -1) state.games[idx] = action.payload;
        const idxp = state.paginatedGames.findIndex((g) => g._id === action.payload._id);
        if (idxp !== -1) state.paginatedGames[idxp] = action.payload;

      })
      .addCase(updateGame.rejected, (state, action) => {
        state.loadingStates.updateGame = false;
        state.error = action.payload;
      })
      // DELETE
      .addCase(deleteGame.pending, (state) => {
        state.loadingStates.deleteGame = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteGame.fulfilled, (state, action) => {
        state.loadingStates.deleteGame = false;
        state.success = "Game deleted successfully";
        state.games = state.games.filter((g) => g._id !== action.payload._id);
        state.paginatedGames = state.paginatedGames.filter((g) => g._id !== action.payload._id);
      })
      .addCase(deleteGame.rejected, (state, action) => {
        state.loadingStates.deleteGame = false;
        state.error = action.payload;
      })
      // RESTORE
      .addCase(restoreGame.pending, (state) => {
        state.loadingStates.restoreGame = true;
        state.error = null;
        state.success = null;
      })
      .addCase(restoreGame.fulfilled, (state, action) => {
        state.loadingStates.restoreGame = false;
        state.success = "Game restored successfully";
        // Remove from current paginatedGames view (which shows deleted games)
        state.paginatedGames = state.paginatedGames.filter((g) => g._id !== action.payload._id);
      })
      .addCase(restoreGame.rejected, (state, action) => {
        state.loadingStates.restoreGame = false;
        state.error = action.payload;
      })
      // Get by ID
      .addCase(getGameById.pending, (state) => {
        state.loadingStates.getGameById = true;
        state.error = null;
      })
      .addCase(getGameById.fulfilled, (state, action) => {
        state.loadingStates.getGameById = false;
        state.singleGame = action.payload.data || null;
      })
      .addCase(getGameById.rejected, (state, action) => {
        state.loadingStates.getGameById = false;
        state.error = action.payload;
      })
      // Get Latest Games by Category
      .addCase(getLatestGamesByCategory.pending, (state) => {
        state.loadingStates.getLatestGamesByCategory = true;
        state.error = null;
      })
      .addCase(getLatestGamesByCategory.fulfilled, (state, action) => {
        state.loadingStates.getLatestGamesByCategory = false;
        state.LatestGamesByCategory = action.payload || [];
      })
      .addCase(getLatestGamesByCategory.rejected, (state, action) => {
        state.loadingStates.getLatestGamesByCategory = false;
        state.error = action.payload;
      })
      // Get All Active Games with Pagination
      .addCase(getAllActiveGamesWithPagination.pending, (state) => {
        state.loadingStates.getAllActiveGamesWithPagination = true;
        state.error = null;
      })
      .addCase(getAllActiveGamesWithPagination.fulfilled, (state, action) => {
        state.loadingStates.getAllActiveGamesWithPagination = false;
        state.paginatedGames = action.payload.data;
        state.totalGames = action.payload.total;
        state.currentPage = action.payload.page;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(getAllActiveGamesWithPagination.rejected, (state, action) => {
        state.loadingStates.getAllActiveGamesWithPagination = false;
        state.error = action.payload;
        state.paginatedGames = [];
        state.totalGames = 0;
        state.currentPage = 1;
        state.totalPages = 1;
      })
      // Get Cloud Games
      .addCase(getCloudGames.pending, (state) => {
        state.loadingStates.getCloudGames = true;
        state.error = null;
      })
      .addCase(getCloudGames.fulfilled, (state, action) => {
        state.loadingStates.getCloudGames = false;
        state.cloudGames = action.payload.data;
      })
      .addCase(getCloudGames.rejected, (state, action) => {
        state.loadingStates.getCloudGames = false;
        state.error = action.payload;
        state.cloudGames = [];
      });
  },
});

export const { clearGameError, clearGameSuccess } = gameSlice.actions;
export default gameSlice.reducer;
